import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useContent } from "../contexts/ContentContext";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { t } = useContent();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const from = location.state?.from?.pathname || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, code });
      login(data.token, data.user);
      toast.success(`Bienvenue ${data.user.name} 👋`);
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-mc py-16" data-testid="login-page">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div className="hidden md:block">
          <p className="overline mb-4">{t("login.overline", "Connexion")}</p>
          <h1 className="font-display font-black text-6xl tracking-tighter leading-[0.95]">
            {t("login.title_line1", "Bon retour")}<br /><span className="text-[hsl(var(--primary))]">{t("login.title_line2", "sur MCZ EVENTS.")}</span>
          </h1>
          <p className="mt-6 text-muted-foreground max-w-md">
            {t("login.subtitle", "Connectez-vous avec votre email et votre code d'accès à 8 caractères.")}
          </p>
          <div className="mt-8 card-mc p-5 bg-[hsl(var(--muted))]/40">
            <p className="overline mb-2">Pas encore de compte ?</p>
            <Link to="/register" className="font-bold hover:text-[hsl(var(--primary))]">Créer mon compte gratuitement →</Link>
          </div>
        </div>

        <form onSubmit={submit} className="card-mc p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-[hsl(var(--primary))] flex items-center justify-center rounded-xl">
              <KeyRound className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-display font-black text-2xl tracking-tighter">Se connecter</p>
              <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Email + Code d'accès</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Email</label>
              <input data-testid="login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full border border-border px-3 py-2.5 rounded-sm bg-card" placeholder="vous@email.com" />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Code d'accès</label>
              <input
                data-testid="login-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="mt-1 w-full border border-border px-3 py-2.5 rounded-sm bg-card font-mono tracking-[0.4em] text-center text-lg"
                placeholder="XXXXXXXX"
                maxLength={12}
              />
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-submit">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Se connecter
            </button>
          </div>

          <p className="text-xs text-muted-foreground mt-6 text-center">
            Pas de compte ? <Link to="/register" className="font-bold underline" data-testid="login-to-register">Créer un compte</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
